import React, { useEffect, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useLocation, useNavigate } from "react-router-dom";
import Loading from "../../Components/Loading";
import ReportNotGenerated from "./ReportNotGenerated";
import { Container } from "react-bootstrap";
import { Breadcrumb } from "@anchor/react-components/dist/lib/components";
import { useCallback } from "react";
import { useRef } from "react";
import SessionValidation from "../../context/SessionValidationContext";
import { PostmortemServices } from "../../services/postmortemServices";
import { ImageServices } from "../../services/imageServices";
import { ChainedFiveWhysService } from "../../services/chainedFiveWhysService";
import { ApprovalWorkflowServices } from "../../services/approvalWorkflowServices";
import UserInfo from "../../context/UserAccessContext";

import Report from "../../Components/Report/Report";
import ReportSectionCommentModal from "../../Components/Report/ReportSectionCommentModal";
import { getComments } from "../../Utils/getCommets";
import { getComments as commentsInApiPayload } from "../../Components/Editors/apiPayloadGeneratingFunctions/getComments";
import { McToast } from "@maersk-global/mds-react-wrapper/components-core/mc-toast";
import { McNotification } from "@maersk-global/mds-react-wrapper/components-core/mc-notification";
import { postmortemApiEndpoint } from "../../config";
import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import ReportDoc from "./ReportDoc";
import { convertEpochToDateFormat } from "../../Utils/convertEpochToDateFormat";
import { fetchMttrInsights } from "../../services/mttrInsightsService";
import { useQueryClient } from "react-query";
import { cachedRequest } from "../../Utils/cachedRequest";
import { fetchIncidentMetadata } from "../../services/metadataService";
// import { useWebSocket } from "../../context/WebSocketContext/WebSocketContext";

function PostmortemReport() {
  const [loading, setLoading] = useState(true);
  const { instance, accounts } = useMsal();
  const accountId = accounts[0]?.homeAccountId;
  const queryClient = useQueryClient();
  const { userIsSreUser, userIsMttsAdmin, userIsAdmin } = UserInfo();
  const [postmortemState, setPostmortemState] = useState("");
  const [postmortemData, setPostmortemData] = useState({});
  const { search } = useLocation();
  const params = new URLSearchParams(search);
  // template-literal URL builders upstream can leak the literal strings "undefined"/"null" when the source record has no incident ID
  const rawIncidentID = params.get("incidentID");
  const incidentID = rawIncidentID && rawIncidentID !== "undefined" && rawIncidentID !== "null" ? rawIncidentID : null;
  const postmortemID = params.get("postmortemID");
  const pageType = params.get("page_type");
  // const pmtId = location.state?.customID || "No data available";
  // const incID = location.state?.incidentID || "No Data available";
  const { setSessionExpired } = SessionValidation();
  const { postPostMortemData, getRcaQuality, fetchEnhancement, fetchCollaborators } = PostmortemServices();
  // const { updateCache } = useWebSocket();
  const updateCache = null; // WebSocket disabled
  const navigate = useNavigate();
  // const

  const [uploadedSummaryImages, setUploadedSummaryImages] = useState([]);
  const [uploadedPrecedingEventsImages, setUploadedPrecedingEventsImages] =
    useState([]);
  const [uploadedImpactImages, setUploadedImpactImages] = useState([]);
  const [
    uploadedDetectionAndResponseImages,
    setUploadedDetectionAndResponseImages,
  ] = useState([]);
  const [uploadedRootCauseImages, setRootCauseUploadedImages] = useState([]);
  const [
    rootCauseIdentificationUploadedImages,
    setRootCauseIdentificationUploadedImages,
  ] = useState([]);
  const [jiraUploadedImages, setJiraUploadedImages] = useState([]);
  const [ptaskUploadedImages, setPtaskUploadedImages] = useState([]);
  const [chainedFiveWhysData, setChainedFiveWhysData] = useState([]);
  const [mttrInsights, setMttrInsights] = useState(null);
  const [incidentMetadata, setIncidentMetadata] = useState(null);

  // Approval workflow state
  const [approvalStatus, setApprovalStatus] = useState(null);
  const [approvalHistory, setApprovalHistory] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [teamsData, setTeamsData] = useState(null);
  const approvalDataFetchedRef = useRef(false);

  // ==================================================================================
  // DEPRECATED HARDCODED LISTS - USED ONLY AS FALLBACK
  // ==================================================================================
  // These lists are OUTDATED and do NOT reflect the actual approvers from backend.
  // The backend dynamically determines approvers from PortfolioMappingConfig based 
  // on Portfolio + Platform, which includes multiple EDs per platform.
  // 
  // Authorization now uses the dynamic `approvalStatus.leadershipReviewApprovers` 
  // list from the backend API as the primary source. These hardcoded lists are 
  // kept only as a fallback for backward compatibility.
  // ==================================================================================
  const ED_APPROVERS = ["k.harish@maersk.com"];  // ❌ INCOMPLETE - Missing many Platform Owner EDs
  const TLT_APPROVERS_BY_PORTFOLIO = {
    "APMT, IOT and Automation": "nilesh.chakkar@maersk.com",
    "Ocean & Logistics": "nilesh.chakkar@maersk.com",
    "Terminal (O&L)": "nilesh.chakkar@maersk.com",
    "B&F & People": "nilesh.chakkar@maersk.com",
    "Default": "nilesh.chakkar@maersk.com",
  };

  let contentRef = useRef();

  const [pdfDownloading, setPdfDownloading] = useState(false);
  const chainedService = ChainedFiveWhysService();

  const handleDownload = async () => {
    setPdfDownloading(true);
    try {
      const docProps = {
        postmortemData,
        uploadedSummaryImages,
        uploadedPrecedingEventsImages,
        uploadedImpactImages,
        uploadedDetectionAndResponseImages,
        uploadedRootCauseImages: uploadedRootCauseImages,
        rootCauseIdentificationUploadedImages,
        jiraUploadedImages,
        ptaskUploadedImages,
        chainedFiveWhysData,
        incidentMetadata,
        mttdMinutes: mttrInsights?.ttd_seconds != null
          ? parseFloat(mttrInsights.ttd_seconds) / 60
          : postmortemData?.detectionResponse?.mttd,
        mttrMinutes: mttrInsights?.ttr_seconds != null
          ? parseFloat(mttrInsights.ttr_seconds) / 60
          : postmortemData?.detectionResponse?.mttrecover,
      };
      const blob = await pdf(<ReportDoc {...docProps} />).toBlob();
      const filename = [
        convertEpochToDateFormat(postmortemData?.createdOn),
        postmortemData?.incidentID,
        postmortemData?.short_description,
      ].filter(Boolean).join("-") || "postmortem-report";
      saveAs(blob, `${filename}.pdf`);
    } catch (err) {
      console.error("PDF generation failed:", err);
      setDataSubmitResponse({
        success: false,
        error: true,
        info: false,
        alertMessage: "PDF generation failed — please try again.",
      });
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setDataSubmitResponse({
        success: true,
        error: false,
        info: false,
        alertMessage: "Link copied to clipboard",
      });
    } catch {
      setDataSubmitResponse({
        success: false,
        error: true,
        info: false,
        alertMessage: "Could not copy link — please copy the URL manually",
      });
    }
  };
  const navigateTo = (path) => {
    navigate(path.path);
  };
  const [dataSubmitResponse, setDataSubmitResponse] = useState({
    success: "",
    error: "",
    info: "",
    alertMessage: "",
  });
  const { fetchPostmortemById } = PostmortemServices();
  const { getImage } = ImageServices();
  const approvalWorkflowService = ApprovalWorkflowServices();
  const apiEndpoint = postmortemApiEndpoint;

  const [commentModalState, setCommentModalState] = useState({
    tabs: [],
    tabsData: [],
    show: false,
    data: null,
    images: [],
    // comments: [],
    dataType: "",
    sectionHeading: "",
  });

  const [comments, setComments] = useState({
    incident_summary: [],
    rootCause: [],
    preventiveCorrectiveMeasures: [],
  });

  useEffect(() => {
    // Check if postmortemData is an object and getComments is a function
    if (
      postmortemData &&
      typeof postmortemData === "object" &&
      typeof getComments === "function"
    ) {
      // Check if postmortemData has keys
      if (Object.keys(postmortemData).length > 0) {
        // Check if the properties of postmortemData are structured as expected
        const incident_summary = postmortemData?.incident_summary?.comments;
        const rootCause = postmortemData?.rootCause?.comments;
        const preventiveCorrectiveMeasures = Array.isArray(
          postmortemData?.preventiveCorrectiveMeasures
        )
          ? postmortemData?.preventiveCorrectiveMeasures[0]?.comments
          : [];

        // Only call setComments if the properties are structured as expected
        if (incident_summary || rootCause || preventiveCorrectiveMeasures) {
          setComments({
            incident_summary: getComments(incident_summary),
            rootCause: getComments(rootCause),
            preventiveCorrectiveMeasures: getComments(
              preventiveCorrectiveMeasures
            ),
          });
        }
      }
    }
  }, [postmortemData]);

  const handleCommentModal = (heading) => {
    if (heading === "Incident Summary") {
      setCommentModalState(() => ({
        tabs: [
          "Incident Summary",
          "Preceding Events",
          "Impact",
          "Detection and Response",
        ],
        tabsData: [
          {
            data: postmortemData?.incident_summary?.summary,
            images: uploadedSummaryImages,
          },
          {
            data: postmortemData?.precedingEvents?.description,
            images: uploadedPrecedingEventsImages,
          },
          {
            data: postmortemData?.businessImpact?.description,
            images: uploadedImpactImages,
          },
          {
            data: postmortemData?.detectionResponse,
            images: uploadedDetectionAndResponseImages,
          },
        ],
        // comments: comments.incident_summary &&  [...comments.incident_summary],
        show: true,
        sectionHeading: "incident_summary",
      }));
    } else if (heading === "Root Cause") {
      setCommentModalState(() => ({
        tabs: ["Recovery", "Root Cause", "Root Cause Identification"],
        tabsData: [
          {
            data: postmortemData?.recovery?.recovery,
            images: [],
          },
          {
            data: postmortemData?.rootCause,
            images: uploadedRootCauseImages,
          },
          {
            data: postmortemData?.rootCauseIdentificationRequest,
            images: rootCauseIdentificationUploadedImages,
          },
        ],
        // comments: comments.rootCause && [...comments.rootCause],
        show: true,
        sectionHeading: "rootCause",
      }));
    } else if (heading === "Preventive And Corrective Measures") {
      setCommentModalState(() => ({
        tabs: ["Jira Tickets", "PTasks"],
        tabsData: [
          {
            data: postmortemData?.preventiveCorrectiveMeasures?.[0]?.jira,
            images: jiraUploadedImages,
          },
          {
            data: postmortemData?.preventiveCorrectiveMeasures?.[0]?.ptask,
            images: ptaskUploadedImages,
          },
        ],
        // comments: comments.preventiveCorrectiveMeasures && [...comments.preventiveCorrectiveMeasures],
        show: true,
        sectionHeading: "preventiveCorrectiveMeasures",
      }));
    }

    // setCommentModalState(() => {
    //   return {
    //     show: true,
    //     data: data,
    //     comments: comments,
    //     images: images,
    //     dataType: "section",
    //     sectionHeading: heading,
    //   };
    // });
  };

  const handleCommentModalClosed = async () => {
    // Prepare the data to send
    // let data = await prepareDataToSend();

    let data;
    try {
      data = await prepareDataToSend();
    } catch (error) {
      console.error("Error preparing data to send:", error);
      return; // Exit the function if data preparation fails
    }

    setDataSubmitResponse({
      success: false,
      error: false,
      info: true,
      alertMessage: "saving comments, please wait...",
    });

    setCommentModalState((currentState) => ({
      ...currentState,
      show: false,
    }));

    // Notify that the upload has started
    // uploadInitiatedNotification();

    try {
      // Make the request and handle the response
      await makeRequestAndHandleResponse(data);
    } catch (error) {
      // Handle any errors that occurred during the request
      handleRequestError(error);
    }
  };

  // Prepares the data to send by adding comments to the postmortem data
  const prepareDataToSend = async () => {
    let dataToSend = { ...postmortemData };

    if (!dataToSend.incident_summary) {
      dataToSend.incident_summary = {};
      dataToSend.incident_summary["comments"] = commentsInApiPayload(
        comments.incident_summary
      );
    } else {
      dataToSend.incident_summary.comments = commentsInApiPayload(
        comments.incident_summary
      );
    }

    if (!dataToSend.rootCause) {
      dataToSend.rootCause = {};
      dataToSend.rootCause.comments = commentsInApiPayload(comments.rootCause);
    } else {
      dataToSend.rootCause.comments = commentsInApiPayload(comments.rootCause);
    }

    // console.log(
    //   "PREVENTIVE AND CORRECTIVE MEASURE S COMMENTS ",
    //   dataToSend.preventiveCorrectiveMeasures.comments,
    //   commentsInApiPayload(comments.preventiveCorrectiveMeasures)
    // );

    if (!dataToSend.preventiveCorrectiveMeasures) {
      dataToSend.preventiveCorrectiveMeasures = [{}];
      dataToSend.preventiveCorrectiveMeasures[0]["comments"] =
        commentsInApiPayload(comments.preventiveCorrectiveMeasures);
    } else {
      dataToSend.preventiveCorrectiveMeasures[0].comments =
        commentsInApiPayload(comments.preventiveCorrectiveMeasures);
    }

    // console.log(
    //   "PREVENTIVE AND CORRECTIVE MEASURE S COMMENTS ",
    //   dataToSend.preventiveCorrectiveMeasures.comments,
    //   commentsInApiPayload(comments.preventiveCorrectiveMeasures),
    //   dataToSend
    // );
    return dataToSend;
  };

  // Makes the request to post the postmortem data and handles the response
  const makeRequestAndHandleResponse = async (dataToSend) => {
    console.log("DATA TO SEND @294", dataToSend);
    const apiResponse = await postPostMortemData(
      apiEndpoint,
      incidentID,
      dataToSend
    );
    if (typeof updateCache === 'function') updateCache(
      dataToSend.customID,
      dataToSend,
    )
    if (apiResponse.status === 200 || apiResponse.status === 201) {
      // setMessage("successfully posted");
      // setDataIsSavedSuccessfully(true);
      // if (mode === "submitting") setDataIsSubmittedSuccessfully(true);
      closeErrorDialoge();
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setDataSubmitResponse({
        success: true,
        error: false,
        info: false,
        alertMessage: "comments saved successfully",
      });
    } else {
      throw new Error("Unknown error");
    }
  };

  // Handles any errors that occurred during the request
  const handleRequestError = async (error) => {
    // toast.done("upload-progress");
    // errorNotification("Error in saving postmortem data");
    closeErrorDialoge();

    // Handle validation errors for acknowledgment requirements
    if (error.response && error.response.status === 400) {
      const errorData = error.response.data;
      if (errorData && (errorData.error || errorData.requiredAction)) {
        const errorMessage = errorData.error || "Validation failed";
        const actionMessage = errorData.requiredAction || "";
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setDataSubmitResponse({
          success: false,
          error: true,
          info: false,
          alertMessage: `${errorMessage}${actionMessage ? " " + actionMessage : ""}`,
        });
        throw error;
      }
    }

    if (error.response && error.response.status === 500) {
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setDataSubmitResponse({
        success: false,
        error: true,
        info: false,
        alertMessage: "error in saving comments, please contact SRE",
      });
      // setStep(step);
    } else {
      console.log("Unknown error");
      // setStep(step);
      await new Promise((resolve) => setTimeout(resolve, 1000));
      setDataSubmitResponse({
        success: false,
        error: true,
        info: false,
        alertMessage: "error in saving comments, please contact SRE",
      });
      console.error(error);
    }

    throw error;
  };

  const fetchAndSetImages = useCallback(
    async (setterFunction, tag) => {
      try {
        const images = await getImage(
          [],
          incidentID,
          tag,
          instance,
          accounts,
          setSessionExpired
        );
        setterFunction(images);
      } catch (error) {
        console.error("Error fetching images:", error);
      }
    },
    [incidentID, instance, accountId, setSessionExpired]
  );

  useEffect(() => {
    if (incidentID) {
      fetchAndSetImages(setUploadedSummaryImages, "summary");
      fetchAndSetImages(setUploadedPrecedingEventsImages, "precedingEvents");
      fetchAndSetImages(setUploadedImpactImages, "impact");
      fetchAndSetImages(
        setUploadedDetectionAndResponseImages,
        "detectionAndResponse"
      );
      fetchAndSetImages(setRootCauseUploadedImages, "rootCause");
      fetchAndSetImages(
        setRootCauseIdentificationUploadedImages,
        "rootCauseIdentification"
      );
      fetchAndSetImages(setPtaskUploadedImages, "ptask");
      fetchAndSetImages(setJiraUploadedImages, "jira");
    }
  }, [incidentID, fetchAndSetImages]);

  useEffect(() => {
    const fetchExecutions = async () => {
      if (postmortemID) {
        try {
          setLoading(true);
          const data = await fetchPostmortemById(apiEndpoint, postmortemID + '?page_type=' + pageType);

          setPostmortemState(data.status);
          setPostmortemData(data);
        } catch (error) {
          console.error(error);
          // setError(true);
        } finally {
          setLoading(false);
        }
      }
    };
    fetchExecutions();
  }, [postmortemID]);

  // Re-reads the report in place (no spinner, no page reload), e.g. after an AI recurrence verdict.
  const refreshPostmortemData = async () => {
    if (!postmortemID) return;
    try {
      const data = await fetchPostmortemById(apiEndpoint, postmortemID + '?page_type=' + pageType);
      setPostmortemData(data);
    } catch (error) {
      console.error(error);
    }
  };

  // Fetch chained 5 whys data after postmortem data loads
  useEffect(() => {
    const fetchChainedData = async () => {
      if (incidentID && instance && accountId) {
        try {
          const chains = await chainedService.getChainedFiveWhysCached(queryClient, incidentID, instance, accounts);
          if (chains && chains.length > 0) {
            setChainedFiveWhysData(chains);
          }
        } catch (error) {
          console.error("Failed to fetch chained 5 whys:", error);
        }
      }
    };

    fetchChainedData();
  }, [incidentID, instance, accountId]);

  // Fetch MTTD / MTTR from insights API (same source as the Report UI)
  useEffect(() => {
    if (!incidentID || !accountId) return;
    cachedRequest(queryClient, ["mttrInsights", incidentID], () => fetchMttrInsights(incidentID, instance, accounts))
      .then((data) => { if (data) setMttrInsights(data); })
      .catch(() => { });
  }, [incidentID, instance, accountId]);

  useEffect(() => {
    if (!incidentID || !accountId) return;
    cachedRequest(queryClient, ["incidentMetadata", incidentID], () => fetchIncidentMetadata(incidentID, instance, accounts))
      .then((data) => { if (data) setIncidentMetadata(data); })
      .catch(() => { });
  }, [incidentID, instance, accountId]);

  // Fetch approval workflow status and Teams data
  useEffect(() => {
    // Reset the fetch flag when postmortemID changes
    approvalDataFetchedRef.current = false;
    
    const fetchApprovalData = async () => {
      if (!postmortemID || approvalDataFetchedRef.current) return;
      
      approvalDataFetchedRef.current = true;
      
      // The three requests are independent, so run them together instead of one after another.
      // Teams/collaborators data is only used for a visibility check (authenticated).
      const teamsRequest = fetchCollaborators(postmortemApiEndpoint, postmortemID)
        .then((teams) => setTeamsData(teams))
        .catch((error) => console.log("Teams data not found for this postmortem:", error.message));

      const [statusResult, historyResult] = await Promise.allSettled([
        approvalWorkflowService.getApprovalStatus(postmortemID),
        approvalWorkflowService.getApprovalHistory(postmortemID),
      ]);
      if (statusResult.status === "fulfilled") setApprovalStatus(statusResult.value);
      if (historyResult.status === "fulfilled") setApprovalHistory(historyResult.value.history || []);

      const failure = [statusResult, historyResult].find((result) => result.status === "rejected");
      if (failure) {
        console.error("Failed to fetch approval data:", failure.reason);
        approvalDataFetchedRef.current = false; // Reset on error to allow retry
      }
      await teamsRequest;
    };

    fetchApprovalData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [postmortemID]);

  // Approval workflow handlers (Simplified - Leadership Review)
  const handleApproveLeadershipReview = async (comment) => {
    console.log("[PostmortemReport] handleApproveLeadershipReview called with comment:", comment);
    
    setApprovalLoading(true);
    try {
      const result = await approvalWorkflowService.approveLeadershipReview(
        instance,
        accounts,
        postmortemID,
        comment
      );
      console.log("[APPROVAL] Leadership Review approved:", result);

      // Determine approver type from result
      const approverType = result.approverType || "Leadership";
      
      setDataSubmitResponse({
        success: true,
        error: false,
        info: false,
        alertMessage: `Leadership Review approved by ${approverType}. Postmortem is now complete.`,
      });

      // Refresh approval data
      const status = await approvalWorkflowService.getApprovalStatus(postmortemID);
      setApprovalStatus(status);
      
      const history = await approvalWorkflowService.getApprovalHistory(postmortemID);
      setApprovalHistory(history.history || []);

      // Reload page to reflect new status
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("Failed to approve Leadership Review:", error);
      setDataSubmitResponse({
        success: false,
        error: true,
        info: false,
        alertMessage: error.response?.data?.error || "Failed to approve Leadership Review",
      });
    } finally {
      setApprovalLoading(false);
    }
  };

  const handleRejectLeadershipReview = async (comment) => {
    console.log("[PostmortemReport] handleRejectLeadershipReview called with comment:", comment);
    
    setApprovalLoading(true);
    try {
      const result = await approvalWorkflowService.rejectLeadershipReview(
        instance,
        accounts,
        postmortemID,
        comment
      );
      console.log("[APPROVAL] Leadership Review rejected:", result);

      setDataSubmitResponse({
        success: true,
        error: false,
        info: false,
        alertMessage: "Leadership Review rejected. Returned to Draft.",
      });

      // Refresh approval data
      const status = await approvalWorkflowService.getApprovalStatus(postmortemID);
      setApprovalStatus(status);
      
      const history = await approvalWorkflowService.getApprovalHistory(postmortemID);
      setApprovalHistory(history.history || []);

      // Reload page to reflect new status
      setTimeout(() => window.location.reload(), 2000);
    } catch (error) {
      console.error("Failed to reject Leadership Review:", error);
      setDataSubmitResponse({
        success: false,
        error: true,
        info: false,
        alertMessage: error.response?.data?.error || "Failed to reject Leadership Review",
      });
    } finally {
      setApprovalLoading(false);
    }
  };

  // Check if current user is authorized to view approval workflow
  const isUserAuthorizedToViewApproval = () => {
    if (!accounts || accounts.length === 0 || !postmortemData) return false;
    
    const userEmail = accounts[0]?.username || accounts[0]?.name || "";
    
    // Check if user is owner
    if (postmortemData.assigned_to === userEmail) return true;
    
    // Check if user is in collaborators (Teams.members)
    if (teamsData && teamsData.members) {
      const isCollaborator = teamsData.members.some(
        member => member.email === userEmail && member.status
      );
      if (isCollaborator) return true;
    }
    
    // PRIORITY 1: Check if user is in the backend's dynamic approvers list (most accurate)
    if (approvalStatus && approvalStatus.leadershipReviewApprovers) {
      if (approvalStatus.leadershipReviewApprovers.includes(userEmail)) {
        return true;
      }
    }
    
    // FALLBACK: Check hardcoded lists for backward compatibility
    // Check if user is ED
    if (ED_APPROVERS.includes(userEmail)) return true;
    
    // Check if user is TLT for this portfolio
    const portfolio = postmortemData.portfolio || "Default";
    const tltApprover = TLT_APPROVERS_BY_PORTFOLIO[portfolio] || TLT_APPROVERS_BY_PORTFOLIO["Default"];
    if (userEmail === tltApprover) return true;
    
    // Check if user is SRE member (read-only access via Teams collection)
    if (userIsSreUser) return true;

    // Check if user is one of the actual stored Leadership Review approvers
    // (Platform Owners + TLT/Portfolio Owner, including manual "Postmortem Approvers" overrides).
    const storedApprovers = approvalStatus?.leadershipReviewApprovers || approvalStatus?.approvers || [];
    if (storedApprovers.some((email) => (email || "").toLowerCase() === userEmail.toLowerCase())) return true;
    
    return false;
  };

  // Check if current user can approve (must be authorized to view AND be an approver)
  const canUserApprove = () => {
    if (!approvalStatus || !accounts || accounts.length === 0) return false;

    const userEmail = (accounts[0]?.username || accounts[0]?.name || "").toLowerCase();

    // Must be in LEADERSHIP_REVIEW stage
    if (approvalStatus.currentStage !== "LEADERSHIP_REVIEW") return false;
    
    // CRITICAL FIX: Use the backend's dynamic approvers list from approval status API
    // This list is populated from PortfolioMappingConfig during submission
    // and contains the actual Platform Owners (EDs) + Portfolio Owner (TLT)
    if (approvalStatus.leadershipReviewApprovers && Array.isArray(approvalStatus.leadershipReviewApprovers)) {
      const isAuthorized = approvalStatus.leadershipReviewApprovers.includes(userEmail);
      console.log(`[APPROVAL_AUTH] User: ${userEmail}, Authorized Approvers: ${approvalStatus.leadershipReviewApprovers}, Can Approve: ${isAuthorized}`);
      return isAuthorized;
    }
    
    // FALLBACK: If backend list is not available, use hardcoded lists (backward compatibility)
    console.warn(`[APPROVAL_AUTH] Backend approvers list not available for ${postmortemID}, falling back to hardcoded lists`);
    
    // Check if user is in the hardcoded ED list
    if (ED_APPROVERS.includes(userEmail)) return true;
    
    // Check if user is TLT for this portfolio
    const portfolio = postmortemData?.portfolio || "Default";
    const tltApprover = TLT_APPROVERS_BY_PORTFOLIO[portfolio] || TLT_APPROVERS_BY_PORTFOLIO["Default"];
    if (userEmail === tltApprover) return true;
    
    return false;
  };

  const canUserEditReport = () => {
    if (!accounts || accounts.length === 0 || !postmortemData) return false;

    const userEmail = (accounts[0]?.username || accounts[0]?.name || "").toLowerCase();
    if (!userEmail) return false;

    // Check owner (assigned_to can be string or object in legacy/updated payloads)
    const assignedTo = postmortemData.assigned_to;
    const ownerEmail =
      typeof assignedTo === "string"
        ? assignedTo
        : assignedTo?.email || assignedTo?.value || assignedTo?.label || "";

    if (ownerEmail && ownerEmail.toLowerCase() === userEmail) return true;

    // Check if user is collaborator (Teams.members)
    if (teamsData && Array.isArray(teamsData.members)) {
      const isCollaborator = teamsData.members.some((member) => {
        const memberEmail = (member?.email || "").toLowerCase();
        return memberEmail === userEmail && member?.status;
      });
      if (isCollaborator) return true;
    }

    // MTTS is the single source of truth for blanket SRE/LT edit access
    // (legacy Mongo-based userIsSreUser bypass intentionally not used here).
    if (userIsMttsAdmin === true) return true;

    return false;
  };

  const handleEditRedirect = () => {
    if (!postmortemID) return;
    navigate(`/editpostmortem?postmortemID=${postmortemID}&page_type=edit`);
  };

  const handleSavePreventiveMeasures = async (updatedPcm) => {
    const dataToSend = { ...postmortemData };
    const existingPcm = (dataToSend.preventiveCorrectiveMeasures && dataToSend.preventiveCorrectiveMeasures[0]) || {};
    dataToSend.preventiveCorrectiveMeasures = [{ ...existingPcm, ...updatedPcm }];

    const apiResponse = await postPostMortemData(apiEndpoint, incidentID, dataToSend);
    if (apiResponse.status === 200 || apiResponse.status === 201) {
      setPostmortemData(dataToSend);
      if (typeof updateCache === "function") updateCache(dataToSend.customID, dataToSend);
      return apiResponse;
    }
    throw new Error("Unknown error");
  };

  if (loading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }
  // if (postmortemState === "NEW" || postmortemState === "PROGRESS") {
  //   return <ReportNotGenerated />;
  // }
  const closeErrorDialoge = () => {
    setDataSubmitResponse({
      info: false,
      error: false,
      success: false,
    });
  };
  const requiredAlertDialog = () => {
    let icon;
    let appearance;

    if (dataSubmitResponse.info) {
      appearance = "info";
      icon = "info-circle";
    } else if (dataSubmitResponse.error) {
      appearance = "error";
      icon = "exclamation-octagon";
    } else if (dataSubmitResponse.success) {
      appearance = "success";
      icon = "check-circle";
    }

    // console.log("ALERT TYPE ", showAlertDialog.alertType);

    return (
      <McToast
        open
        position="top-center"
        appearance={appearance}
        close={closeErrorDialoge}
        duration="5000"
        style={{ marginTop: "20px" }}
      >
        <McNotification
          icon={icon}
          body={dataSubmitResponse.alertMessage}
        ></McNotification>
      </McToast>
    );
  };

  let alertDia = requiredAlertDialog();
  // console.log
  return (
    <>
      {dataSubmitResponse.success ||
        dataSubmitResponse.error ||
        dataSubmitResponse.info ? (
        alertDia
      ) : (
        <></>
      )}
      <div ref={(el) => (contentRef = el)}>
        <div
          style={{
            paddingLeft: "20px",
          }}
        >
          <Breadcrumb
            activeItem="report"
            className="breadcrumb-story"
            mainObject={{
              editProfile: {
                breadcrumbLocator: "home.editProfile",
                label: "Ongoing Postmortems",
                path: `/ongoing-postmortems`,
              },
              home: {
                breadcrumbLocator: "home",
                label: "Home",
                path: "/",
              },
              report: {
                breadcrumbLocator: "home.editProfile.report",
                label: "Report",
                path: "/",
              },
            }}
            onBreadcrumbClick={navigateTo}
            separator="/"
          />
        </div>
        <Container
          fluid
          style={{
            maxWidth: "90%",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            alignItems: "stretch",
          }}
          className="pt-3 px-4 pb-5"
        >
          {/* Main Report */}
          <Report
            postmortemData={postmortemData}
            uploadedSummaryImages={uploadedSummaryImages}
            uploadedPrecedingEventsImages={uploadedPrecedingEventsImages}
            uploadedImpactImages={uploadedImpactImages}
            uploadedDetectionAndResponseImages={
              uploadedDetectionAndResponseImages
            }
            uploadedRootCauseImages={uploadedRootCauseImages}
            rootCauseIdentificationUploadedImages={
              rootCauseIdentificationUploadedImages
            }
            jiraUploadedImages={jiraUploadedImages}
            ptaskUploadedImages={ptaskUploadedImages}
            handleCommentModal={handleCommentModal}
            fetchRcaQuality={(incidentID, forceRefresh) => getRcaQuality(apiEndpoint, incidentID, forceRefresh)}
            postMortemId={postmortemID}
            incidentID={incidentID}
            fetchEnhancements={(id) => fetchEnhancement(id)}
            onRecurrenceChanged={refreshPostmortemData}
            onShare={handleShare}
            onExportPdf={handleDownload}
            onEdit={handleEditRedirect}
            canUserEditReport={canUserEditReport}
            userIsAdmin={userIsAdmin}
            onSavePreventiveMeasures={handleSavePreventiveMeasures}
            pdfDownloading={pdfDownloading}
            approvalStatus={approvalStatus}
            approvalHistory={approvalHistory}
            approvalLoading={approvalLoading}
            canUserApprove={canUserApprove}
            isUserAuthorizedToViewApproval={isUserAuthorizedToViewApproval}
            handleApproveLeadershipReview={handleApproveLeadershipReview}
            handleRejectLeadershipReview={handleRejectLeadershipReview}
          />
        </Container>
      </div>

      <ReportSectionCommentModal
        modalState={commentModalState}
        setCommentsToSend={setComments}
        comments={comments}
        handleCommentModal={handleCommentModal}
        handelModalClosed={handleCommentModalClosed}
      />
    </>
  );
}

export default PostmortemReport;
